/**
 * Event Costs Report Page
 *
 * Shows cost breakdown per event for a date range.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getEventCostsReport, downloadReportCSV, EventCostData } from '../../../api/reports';
import { getErrorMessage } from '../../../api/client';
import { ReportFilters, ReportSummaryCard, useReportDateRange } from '../../../components/reports';
import { formatInTimeZone } from '../../../utils/dateFormatting';

export default function EventCostsPage() {
  const [dateRange, setDateRange] = useReportDateRange();
  const [report, setReport] = useState<EventCostData[]>([]);
  const [totals, setTotals] = useState({ totalCost: 0, totalHours: 0, eventCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getEventCostsReport({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
      });
      setReport(response.report);
      setTotals(response.totals);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadReportCSV(
        '/reports/event-costs',
        {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        },
        'event-costs.csv'
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return formatInTimeZone(dateString, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div>
      <nav className="mb-4">
        <Link to="/admin/reports" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Reports
        </Link>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Event Costs Report</h1>
        <p className="text-gray-600 mt-1">Cost breakdown per event</p>
      </div>

      <ReportFilters
        dateRange={dateRange}
        onChange={setDateRange}
        onExportCSV={handleExport}
        isExporting={isExporting}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <ReportSummaryCard label="Total Cost" value={formatCurrency(totals.totalCost)} />
          <ReportSummaryCard label="Total Hours" value={totals.totalHours.toFixed(1)} />
          <ReportSummaryCard label="Events" value={totals.eventCount.toString()} />
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading report...</p>
          </div>
        ) : report.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="text-sm font-medium text-gray-900">No data found</h3>
            <p className="mt-1 text-sm text-gray-500">No event cost data for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sport/Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Crew</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {report.map((row) => (
                  <tr key={row.eventId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link to={`/admin/events/${row.eventId}`} className="font-medium text-gray-900 hover:text-primary-600">
                        {row.eventName}
                      </Link>
                      <p className="text-sm text-gray-500">{row.venue}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDate(row.eventDate)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {row.sport || row.eventType || '-'}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.crewCount}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.totalHours.toFixed(1)}</td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(row.totalCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={4} className="px-6 py-3 text-sm font-medium text-gray-900">Total</td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">{totals.totalHours.toFixed(1)}</td>
                  <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">{formatCurrency(totals.totalCost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
